import styled, { css } from 'styled-components';
import { exFont } from '../../shared/typography';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 888px;
  margin: 0 auto;
  padding: 28px 28px 48px;
  box-sizing: border-box;
`;

export const Heading = styled.h2`
  ${exFont(700, 'xxl')}
  line-height: normal;
  margin: 0;
  color: var(--ex-ink);
`;

const dropBase = css`
  display: flex;
  align-items: center;
  border: 2px dashed var(--ex-purple-200);
  border-radius: var(--ex-radius-card);
  background: ${({ $over }) =>
    $over ? 'var(--ex-surface-tint)' : 'var(--ex-surface)'};
  cursor: pointer;
  &:hover {
    background: var(--ex-surface-tint);
  }
`;

export const DropFirst = styled.div`
  ${dropBase}
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  min-height: 296px;
  border-width: 1px;
  & > button {
    min-height: 48px;
    min-width: 146px;
    padding: 0 32px;
  }
  box-sizing: border-box;
  padding: 48px 24px;
  text-align: center;
`;

export const DropMore = styled.div`
  ${dropBase}
  gap: 16px;
  padding: 22px 24px;
  border-width: 1.5px;
  p:first-child {
    font-size: 15px;
    font-weight: 600;
    line-height: normal;
  }
  p:nth-child(2) {
    font-size: 13px;
    font-weight: 400;
    line-height: normal;
  }
`;

export const DropIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ex-purple);
  ${({ $more }) =>
    $more &&
    css`
      width: 46px;
      height: 46px;
      flex: 0 0 auto;
      border-radius: 12px;
      background: var(--ex-purple-100);
    `}
  ${({ $large }) =>
    $large &&
    css`
      width: 56px;
      height: 56px;
      border-radius: var(--ex-radius-card);
      background: var(--ex-purple-100);
    `}
`;

export const Text = styled.p`
  ${exFont(500, 's')}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const Strong = styled.p`
  ${exFont(600, 'm')}
  margin: 0;
  color: var(--ex-ink);
`;

export const Muted = styled.p`
  ${exFont(400, 'xs')}
  margin: 0;
  color: var(--ex-ink-muted);
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
  background: var(--ex-surface);
  overflow: hidden;
  ${({ $upload }) =>
    $upload &&
    css`
      padding: 18px 22px;
      gap: 14px;
    `}
`;

export const CardHead = styled.div`
  ${exFont(600, 's')}
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  font-size: 15px;
  line-height: normal;
  color: var(--ex-ink);
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--ex-line);
  &:last-child {
    border-bottom: none;
  }
  ${({ $file }) =>
    $file &&
    css`
      padding: 0;
      border: 0;
      gap: 14px;
      & > svg {
        color: var(--ex-bad);
        width: 22px;
        height: 22px;
      }
    `}
`;

export const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
`;

export const RowLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

export const RowName = styled.span`
  ${exFont(500, 's')}
  font-size: 14px;
  line-height: normal;
  color: var(--ex-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMeta = styled.span`
  ${exFont(400, 'xs')}
  font-size: 12.5px;
  line-height: normal;
  color: ${({ $bad }) => ($bad ? 'var(--ex-bad-ink)' : 'var(--ex-ink-muted)')};
  white-space: nowrap;
`;

export const Bar = styled.div`
  height: 5px;
  border-radius: 2px;
  background: var(--ex-surface-2);
  overflow: hidden;
  & > span {
    display: block;
    height: 100%;
    background: ${({ $complete }) =>
      $complete ? 'var(--ex-ok)' : 'var(--ex-purple)'};
    transition: width 0.2s ease;
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: none;
  color: var(--ex-ink-muted);
  cursor: pointer;
  &:hover {
    background: var(--ex-surface-2);
    color: var(--ex-ink);
  }
`;

export const TypedBadge = styled.span`
  ${exFont(700, 'xs')}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: var(--ex-purple);
  background: var(--ex-purple-100);
`;

export const Question = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 16px;
  flex-wrap: wrap;
  border-radius: var(--ex-radius-card);
  border: 1px solid
    ${({ $highlight }) => ($highlight ? 'var(--ex-bad)' : 'var(--ex-line)')};
  background: ${({ $highlight }) =>
    $highlight ? 'var(--ex-bad-bg)' : 'var(--ex-surface)'};
  & > div:first-child {
    flex: 1;
    min-width: 240px;
  }
  & > div:first-child > p:first-child {
    font-size: 14px;
    line-height: normal;
    color: var(--ex-purple);
  }
  & > div:first-child > p:nth-child(2) {
    font-size: 13px;
    line-height: 1.45;
    margin-top: 2px;
  }
`;

export const Required = styled.span`
  color: var(--ex-bad);
`;

export const Choice = styled.div`
  display: flex;
  gap: 8px;
`;

export const ChoiceButton = styled.button`
  ${exFont(600, 's')}
  min-height: 36px;
  padding: 0 18px;
  font-size: 14px;
  border-radius: var(--ex-radius-pill);
  cursor: pointer;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line-strong)')};
  color: ${({ $active }) =>
    $active ? 'var(--white)' : 'var(--ex-ink-strong)'};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-surface)'};
`;

export const LinkButton = styled.button`
  ${exFont(600, 's')}
  border: none;
  background: none;
  padding: 0;
  color: var(--ex-purple);
  cursor: pointer;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
`;

export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
  background: var(--ex-surface);
`;

export const FormHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const field = css`
  ${exFont(400, 's')}
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: var(--ex-radius-item);
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line-strong)')};
  background: var(--ex-surface);
  color: var(--ex-ink);
  &:focus {
    outline: none;
    border-color: var(--ex-purple);
  }
`;

export const Input = styled.input`
  ${field}
`;

export const Select = styled.select.attrs({ className: 'inset-select-arrow' })`
  ${field}
`;

export const TextArea = styled.textarea`
  ${field}
  min-height: 140px;
  resize: vertical;
`;

export const FormFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
`;

export const Banner = styled.div`
  ${exFont(500, 's')}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: var(--ex-radius-item);
  color: ${({ $tone }) =>
    $tone === 'info' ? 'var(--ex-purple)' : 'var(--ex-bad-ink)'};
  background: ${({ $tone }) =>
    $tone === 'info' ? 'var(--ex-purple-100)' : 'var(--ex-bad-bg)'};
`;

export const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 560px;
  margin: 48px auto;
  padding: 32px 28px;
  text-align: center;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
`;

export const Icon = styled.img`
  width: 48px;
  height: 48px;
`;

export const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--ex-surface-2);
  overflow: hidden;
  & > span {
    display: block;
    height: 100%;
    background: var(--ex-purple);
  }
`;

export const Hidden = styled.input`
  display: none;
`;

export const TypedAction = styled.div`
  text-align: center;
`;
